import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';

import { Box, IconButton, Menu, MenuItem, Switch } from '@mui/material';
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { SCUIContent } from '../../atomic/organisms';
import { SCUIBreadCrumb, SCUICustomDataGrid } from '../../atomic/molecules';
import { SCUIButton, SCUILoadingLinear } from '../../atomic/atoms';

import { PageUrl } from '../../types/enums';
import { OrderPrioDrawer } from './components/OrderPrioDrawer';

import OrderPrioQuery from '../../services/query/orderPrio/OrderPrioQuery.services';
import TimeHelper from '../../utils/time';
import { OP } from './orderPrio.classes';

export function OrderPrio() {
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
    const [editData, setEditData] = useState<any>({});
    const [pageSize, setPageSize] = useState<number>(10);
    const [pageNumber, setPageNumber] = useState<number>(0);
    const [gridData, setGridData] = useState([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [prioTypes, setPrioTypes] = useState<any[]>([]);
    const [prioReasons, setPrioReasons] = useState<any[]>([]);

    const { t } = useTranslation();

    const handleDrawerClose = () => {
        setIsDrawerOpen(false);
        setEditData({});
    };

    const handleEdit = (data: any) => {
        setEditData(data);
        setIsDrawerOpen(true);
    };

    const columnData: any = [
        {
            field: 'priorityReasonId',
            headerName: t('orderPrio.prioReason'),
            flex: 1,
            renderCell: (params: any) => {
                const rowReason = (prioReasons || [])?.find((item: any) => item.id === params.row?.priorityReasonId);
                return <p> {rowReason?.description || '-'}</p>;
            },
        },
        {
            field: 'priorityTypeId',
            headerName: t('orderPrio.prioType'),
            flex: 1,
            renderCell: (params: any) => {
                const rowType = (prioTypes || [])?.find((item: any) => item.id === params.row?.priorityTypeId);
                return <p> {rowType?.description || '-'}</p>;
            },
        },
        {
            field: 'isActive',
            headerName: t('orderPrio.activeStatus'),
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            width: 100,
            renderCell: (params: any) => {
                return (
                    <div>
                        <Switch checked={params.row.isActive} />
                        {params.row.isActive}
                    </div>
                );
            },
        },
        {
            field: 'startDate',
            headerName: t('orderPrio.startDate'),
            width: 200,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => {
                if (params.row.startDate) {
                    return TimeHelper.formatDateHour(params.row.startDate || '');
                }
                return <>-</>;
            },
        },
        {
            field: 'endDate',
            headerName: t('orderPrio.endDate'),
            width: 200,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => {
                if (params.row.endDate) {
                    return TimeHelper.formatDateHour(params.row.endDate || '');
                }
                return <>-</>;
            },
        },
        {
            field: 'lastUpdateDate',
            headerName: t('orderPrio.updatedDate'),
            width: 200,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => {
                return TimeHelper.formatDateHour(params.row.lastUpdateDate || '');
            },
        },
        {
            field: 'detay',
            headerName: t('general.detail'),
            align: 'center',
            headerAlign: 'center',
            renderCell: (cellData: any) => {
                return (
                    <PopupState variant="popover" popupId="demo-popup-menu">
                        {(popupState: any) => (
                            <>
                                <IconButton {...bindTrigger(popupState)}>
                                    <MoreVertIcon className="svg_icons" />
                                </IconButton>
                                <Menu {...bindMenu(popupState)}>
                                    <MenuItem onClick={() => handleEdit(cellData?.row)}>{t('general.edit')}</MenuItem>
                                </Menu>
                            </>
                        )}
                    </PopupState>
                );
            },
        },
    ];

    const getGridData = () => {
        setIsLoading(true);
        OrderPrioQuery.GetOrderPrioGetOrderPrioList()
            .then((res: any) => {
                if (res) {
                    setGridData(res.data?.orderPriorityList);
                }
                setIsLoading(false);
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured'), '', 'error');
                setIsLoading(false);
            });
    };

    const getDrawerSelectData = () => {
        OrderPrioQuery.GetOrderPrioReasons()
            .then((res: any) => {
                if (res) {
                    setPrioReasons(res.data?.orderPriorityReasons);
                }
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured'), '', 'error');
            });

        OrderPrioQuery.GetOrderPrioTypes()
            .then((res: any) => {
                if (res) {
                    setPrioTypes(res.data?.orderPriorityStatusList);
                }
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured'), '', 'error');
            });
    };

    useEffect(() => {
        getGridData();
        getDrawerSelectData();
    }, []);

    return (
        <SCUIContent className={OP.root}>
            {isLoading ? (
                <div className={OP.loadingWrap}>
                    <SCUILoadingLinear />
                </div>
            ) : (
                <></>
            )}
            <SCUIBreadCrumb title={t('orderPrio.title')} componentLink={PageUrl.orderPrio.url} />
            <Box className={OP.gridWrap} sx={{ display: 'flex', flexDirection: 'column', marginTop: '16px' }}>
                <SCUIButton
                    text={t('orderPrio.addNew')}
                    type="primary"
                    sizeType="fixed"
                    size={'s'}
                    style={{ width: '250px', marginBottom: '16px', marginLeft: 'auto' }}
                    className={OP.addNewButton}
                    onClick={() => setIsDrawerOpen(true)}
                />
                <div>
                    <SCUICustomDataGrid
                        onPageSizeChange={(i: number) => {
                            setPageSize(i);
                        }}
                        pageSize={pageSize}
                        onPageChange={(i: number) => {
                            setPageNumber(i);
                        }}
                        getRowId={(row: any) => row.orderPriorityListId}
                        page={pageNumber}
                        rows={gridData || []}
                        columns={columnData}
                    />
                </div>
            </Box>
            <OrderPrioDrawer
                editData={editData}
                isDrawerOpen={isDrawerOpen}
                handleDrawerClose={handleDrawerClose}
                getGridData={getGridData}
                prioReasons={prioReasons || []}
                prioTypes={prioTypes || []}
            />
        </SCUIContent>
    );
}
